/**
 * One audit record as a list row — the Step A visits-panel row, moved out
 * of `Mandates.tsx` unchanged so the "Show every visit" list and the
 * one-record rows of the grouped list (orders, signed mandates) render the
 * same card (labels-v2 Step B).
 */

import { humanRecordDevice, identityState, isHumanVisit } from '@sill/audit';
import { isProbePath } from '@sill/types';

import { Pill, RelativeTime, Tooltip } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { effectiveOutcome, shortPolicyLabel } from './effective-outcome.js';
import { recordHeadline } from './intent-label.js';
import { isSillToolVisit, siteIsWordPress, visitListName, visitMark } from './visit-identity.js';
import { SiteCtx } from '../../app/providers.js';
import { useContext } from 'react';
import { ProbingPill } from './ProbingPill.js';
import { UcpPlatformTrustMark } from './MandateDetailV3.js';
import { isUcpOrder } from './ucp.js';
import { VisitIdentityMark, VisitLogoGlyph } from './VisitIdentityMarks.js';
import type { MandateRecord } from './types.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface VisitRecordRowProps {
  readonly record: MandateRecord;
  readonly isSelected: boolean;
  readonly isNew: boolean;
  readonly isFlash: boolean;
  readonly isLast: boolean;
  /** Per-agent all-time roll-up ("N visits · N actions"), when loaded. */
  readonly agentStats?: { visit_count: number; action_count: number } | undefined;
  readonly onSelect: () => void;
}

export function VisitRecordRow({
  record: r,
  isSelected: isSel,
  isNew,
  isFlash,
  isLast,
  agentStats: stats,
  onSelect,
}: VisitRecordRowProps): JSX.Element {
  const C = useC();
  const site = useContext(SiteCtx) ?? { activeSite: null };
  // Single collapsed verdict — folds the policy decision AND
  // the settlement outcome into one pill so an APPROVED-but-
  // blocked-at-settlement order (r31) no longer shows a
  // contradictory "APPROVED + DECLINED" pair. See
  // `effective-outcome.ts`.
  const rowPresented = effectiveOutcome(r);
  // Visits panel Step A (labels-v2): identity comes ONLY from
  // the server verdict via `identityState()`. Confirmed gets a
  // quiet mint shield; Mismatch gets a coral shield, quotes, a
  // generic coral Bot glyph and a coral left edge. Everything
  // else draws nothing.
  const idResult = identityState(r);
  const isMismatch = idResult.state === 'mismatch';
  const headline = visitListName(recordHeadline(r), idResult);
  // A human visit: "Visitor from Austin", line 2 "Chrome on Windows".
  const device = isHumanVisit(r) ? humanRecordDevice(r) : null;
  // OBSERVED is implicit (the 95% case of Discovery rows).
  // Only render the decision pill for non-default decisions
  // — APPROVED/REJECTED/ESCALATED carry real signal.
  const showDecisionPill = rowPresented.label !== 'OBSERVED';
  // ADR-0057 — show the UNREGISTERED ORIGIN pill only on a row
  // whose embed beacon came from a KNOWN host that did not match
  // the site's registered domain or allow-list (host mismatch).
  // A no-referrer beacon has no origin to register/allow-list, so
  // it is NOT an "unregistered origin" — suppress the pill there
  // (requires a non-null observed_host). METADATA on the row (not
  // part of the signed envelope); the tooltip surfaces the host.
  const isUnregisteredOrigin =
    r.origin_match !== undefined
    && r.origin_match.matched === false
    && r.origin_match.observed_host !== null
    && r.origin_match.observed_host !== '';
  const unregisteredOriginTitle = isUnregisteredOrigin
    ? `Recorded from ${r.origin_match!.observed_host} — not a registered origin for this site.`
    : undefined;
  // ops2026 UCP checkout test (2026-10-21): a staff-run test shows a
  // neutral "SILL TEST" pill so a merchant can tell the test apart from a
  // real-looking order on the Activity list. Unsigned flag off the server
  // (see `services/api/src/ucp/staff-test.ts`); omitted on every other row.
  const isStaffTest = r.staff_test === true;
  const showAnyPills =
    showDecisionPill
    || isNew
    || isUnregisteredOrigin
    || isStaffTest;
  // Step C: a visit whose page is a probe path (/.env, /wp-login.php…).
  // Never on a visit Sill confirmed (security review M2), and never under a
  // registry name (founder feedback 2026-10-14: those probes come from
  // other senders borrowing the name). Quiet pill only, no coral edge.
  // WordPress pages are not probes on a WordPress site (founder
  // 2026-10-14). An unverified Sill tool visit counts only universal probe
  // classes (security review m1): it is classified as if on WordPress.
  const sillTool = isSillToolVisit(r);
  const probing = idResult.state !== 'confirmed' && !isMismatch
    && (r.identification_match?.agent_id === undefined || sillTool)
    && typeof r.page_path === 'string' && r.page_path.length > 0
    && isProbePath(r.page_path, { wordpress: sillTool || siteIsWordPress(site.activeSite) });
  // Left edge: coral on Mismatch (always, selected or not); lime when NEW
  // or selected; otherwise none.
  const edgeColor = isMismatch
    ? C.coralFg
    : isNew || isSel
      ? C.limeFg
      : 'transparent';
  return (
    <button
      type="button"
      data-record-id={r.record_id}
      data-identity-state={idResult.state}
      data-probing={probing ? 'true' : undefined}
      onClick={onSelect}
      className="w-full p-4 text-left"
      style={{
        background: isNew
          ? (isSel ? C.surface2 : 'rgba(217, 255, 63, 0.06)')
          : (isSel ? C.surface2 : 'transparent'),
        borderLeft: `2px solid ${edgeColor}`,
        borderBottom: !isLast ? `1px solid ${C.borderSoft}` : 'none',
        // Deep-link anchor flash — a transient inset ring,
        // fading out over 600ms.
        boxShadow: isFlash
          ? `inset 0 0 0 2px ${isMismatch ? C.coralFg : C.limeFg}`
          : 'inset 0 0 0 0 transparent',
        transition: 'background-color 800ms ease-out, border-left-color 800ms ease-out, box-shadow 600ms ease-out',
      }}
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          {/* `verification_rejected` rows render the coral
              ShieldAlert so the icon matches the coral REJECTED
              pill; Mismatch renders a generic coral Bot (never
              the impersonated brand); everything else the
              claimed agent's logo. */}
          <VisitLogoGlyph record={r} result={idResult} size={14} />
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              fontWeight: 600,
            }}
          >
            {headline}
          </span>
          <VisitIdentityMark record={r} result={idResult} />
          {probing ? <ProbingPill /> : null}
          {/* UCP order (founder 2026-10-21): parity with VisitGroupRow — the
              signed-and-verified mint shield lives on the list row too, so
              a merchant reads the same mark on both surfaces. One shield
              only: skip it when the identity mark already drew one. */}
          {isUcpOrder(r) && visitMark(idResult) === null ? <UcpPlatformTrustMark C={C} surface="row" /> : null}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {(r.visit_count ?? 1) > 1 ? (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.skyFg,
                letterSpacing: 0.5,
              }}
              aria-label={`${r.visit_count} visits aggregated`}
            >
              ×{(r.visit_count ?? 1).toLocaleString('en-US')}
            </span>
          ) : null}
          <RelativeTime
            iso={
              (r.visit_count ?? 1) > 1 && r.last_visit_at !== undefined
                ? r.last_visit_at
                : r.evaluated_at
            }
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              /* C.dim fails text contrast in both themes
                 (~2.0:1 dark, ~2.7:1 light). Use C.muted for
                 timestamps — labels-review/ux.md §10.4. */
              color: C.muted,
            }}
          />
        </div>
      </div>
      {device !== null ? (
        <div
          data-testid="visit-human-device"
          style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginBottom: 4 }}
        >
          {device}
        </div>
      ) : null}
      {showAnyPills ? (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {showDecisionPill ? (
            <Pill tone={rowPresented.tone}>{rowPresented.label}</Pill>
          ) : null}
          {isUnregisteredOrigin ? (
            <Tooltip content={unregisteredOriginTitle}>
              <Pill tone="amber">UNREGISTERED ORIGIN</Pill>
            </Tooltip>
          ) : null}
          {isStaffTest ? (
            <Tooltip content="Run from Sill's ops console to check this site's checkout. Not counted in your numbers.">
              <Pill tone="neutral">SILL TEST</Pill>
            </Tooltip>
          ) : null}
          {isNew ? <Pill tone="lime">NEW</Pill> : null}
        </div>
      ) : null}
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          wordBreak: 'break-all',
        }}
      >
        {r.record_id}
      </div>
      <Tooltip content={`Evaluated against policy version ${r.policy_version}`} displayMode="block">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.dim,
          wordBreak: 'break-all',
          marginTop: 2,
        }}
      >
        {shortPolicyLabel(r.policy_version)}
      </div>
      </Tooltip>
      {/* Per-agent roll-up. Hidden on Mismatch: the counts belong to
          the real agent whose name this sender used (ADR-0229 §9). */}
      {!isMismatch && stats !== undefined ? (
        <div
          className="flex items-center gap-3"
          style={{ marginTop: 4 }}
        >
          <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
            <span style={{ fontWeight: 700, color: C.text }}>{stats.visit_count.toLocaleString()}</span>
            {' '}visits
          </span>
          {stats.action_count > 0 ? (
            <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
              <span style={{ fontWeight: 700, color: C.limeFg }}>{stats.action_count.toLocaleString()}</span>
              {' '}actions
            </span>
          ) : null}
        </div>
      ) : null}
    </button>
  );
}
